import { createPopper, Instance as PopperInstance } from '@popperjs/core';
import { getElementByClassName } from '../../utils';
import { SearchInput } from './../SearchInput';
import { DropdownItem } from './DropdownItem';

interface DropdownState {
  isOpen: boolean;
  items: DropdownItem[];
  selectedItem?: DropdownItem;
}

export class Dropdown {
  private static showAttribute = 'data-opened';

  private readonly _dropdown: Element;
  private readonly _control: HTMLElement;
  private readonly _content: HTMLElement;
  private readonly _popper: PopperInstance;
  private readonly _search: SearchInput | undefined;
  private readonly _label: HTMLElement;
  private readonly _clearButton: HTMLElement;
  private readonly _input: HTMLInputElement;
  private readonly _originalLableText: string;

  private _state: DropdownState;

  constructor(dropdownElement: Element) {
    this._state = {
      isOpen: false,
      items: [],
    };

    this._dropdown = dropdownElement;
    this._control = getElementByClassName(dropdownElement, 'dropdown__button');
    this._content = getElementByClassName(dropdownElement, 'dropdown__content');
    this._label = this._control.getElementsByTagName('span')[0];
    this._clearButton = dropdownElement.querySelector('[data-is-clear-icon]')!;
    this._input = dropdownElement.querySelector('[data-is-dropdown-input]')!;

    this._originalLableText = this._label.innerText;

    const searchInput = $(this._content)
      .find('.dropdown__search')
      .find('input');
    if (searchInput && searchInput.length === 1) {
      const hideResultsOnInit = this.getCurrentItemElements().length === 0;
      this._search = new SearchInput(searchInput[0], {
        hideResultsOnInit,
        onSearched: () => this.initItems(),
      });
    }

    this._popper = this.createPopperInstance();

    this.setupEventListeners();
    this.initItems();
  }

  private setupEventListeners() {
    this._control.addEventListener('click', () => {
      if (this._state.isOpen) {
        this.hide();
      } else {
        this.show();
        this._search?.focus();
      }
    });

    this._clearButton.addEventListener('click', this.handleClearClicked);

    $(document).on('click', (e) => this.closePopupOnClickOutside(e));
  }

  private initItems() {
    // destroy previous items
    for (const item of this._state.items) {
      item.destroy();
    }
    // clear state
    this._state.items = [];

    // create new items
    const items = this.getCurrentItemElements();

    for (const item of items) {
      const dropdownItem = new DropdownItem(item, {
        onClick: this.handleDropdownItemSelected,
      });
      this._state.items.push(dropdownItem);

      if (dropdownItem.isSelected()) {
        this.selecteItem(dropdownItem);
      }
    }
  }

  private getCurrentItemElements() {
    return getElementByClassName(
      this._content,
      'dropdown__items'
    ).getElementsByClassName('dropdown__item');
  }

  private show() {
    this._dropdown.setAttribute(Dropdown.showAttribute, '');
    this._popper.update();
    this._state.isOpen = true;
  }

  private hide() {
    this._dropdown.removeAttribute(Dropdown.showAttribute);
    this._state.isOpen = false;
  }

  private selecteItem = (item: DropdownItem) => {
    this._state.selectedItem?.deselect();
    this._state.selectedItem = item;
    this._state.selectedItem.select();

    this._label.innerText = item.getValue();
    this._input.value = item.getKey();
    this.hide();
    this._dropdown.setAttribute('data-has-data', '');
  };

  private handleDropdownItemSelected = (item: DropdownItem) => {
    this.selecteItem(item);
    this.triggerChange();
  };

  private handleClearClicked = (e: Event) => {
    this._label.innerText = this._originalLableText;
    this._input.value = '';
    this._dropdown.removeAttribute('data-has-data');
    this._state.selectedItem?.deselect();
    e.stopPropagation();
    this.triggerChange();
  };

  private closePopupOnClickOutside(e: JQuery.ClickEvent) {
    const target = e.target as unknown as Element;
    if (
      !$(this._dropdown).has(target).length &&
      !$(this._content).has(target).length
    ) {
      this.hide();
    }
  }

  private triggerChange() {
    $(this._input).trigger('change');
  }

  private createPopperInstance() {
    const arrow = getElementByClassName(this._content, 'dropdown__arrow');
    return createPopper(this._control, this._content, {
      placement: 'bottom-start',
      modifiers: [
        {
          name: 'arrow',
          options: {
            element: arrow,
          },
        },
        {
          name: 'flip',
          enabled: false,
        },
        {
          name: 'offset',
          options: {
            offset: [0, 8],
          },
        },
      ],
    });
  }
}
